import React, { useCallback, useContext, useMemo, useState } from "react"
import { Children } from "../../../../../../../../../components/miscellianous/children"
import { AssignedParking } from "../../../../../../../../../core/dto/beem-shot/BSBuildingLevel/AssignedParking"
import { BSParking } from "../../../../../../../../../core/dto/beem-shot/BSParking/BSParking"

import { SelectObjOption } from "../../../../../../../../../components/inputs/select-input/SelectObjectInput"
import { BSParkingListContext } from "../../BSBuildingParkingPage/context/BSParkingListContext"
import { BSLevelFormContext } from "./BSLevelFormContext"

export const ParkingAssignmentContext = React.createContext<ParkingAssignmentStore>({} as ParkingAssignmentStore)

export function ParkingAssignmentContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { superstructureParkingList, infrastructureParkingList } = useContext(BSParkingListContext)
  const { selectedGroupLevel } = useContext(BSLevelFormContext)

  const [parkingErrors, setParkingErrors] = useState<Record<string, string | undefined>>({})
  const [isEditing, setIsEditing] = useState<boolean>(false)

  const superstructureOptions = useMemo(() => {
    if (selectedGroupLevel?.levelList[0].bsParkingList?.length === undefined || isEditing) {
      return superstructureParkingList.map((parking) => ({ label: parking.name, value: parking }))
    } else {
      return superstructureParkingList
        .filter((parking) =>
          selectedGroupLevel?.levelList[0].bsParkingList?.every(
            (assignedParking) => assignedParking.parkingId !== parking.id
          )
        )
        .map((parking) => ({ label: parking.name, value: parking }))
    }
  }, [isEditing, selectedGroupLevel?.levelList, superstructureParkingList])

  const infrastructureOptions = useMemo(
    () => infrastructureParkingList.map((parking) => ({ label: parking.name, value: parking })),
    [infrastructureParkingList]
  )

  const clearError: (fieldName: string) => void = useCallback(
    (fieldName) => {
      setParkingErrors((prev) => {
        const { [fieldName]: _, ...rest } = prev
        return rest
      })
    },
    [setParkingErrors]
  )

  const validate1To100: (fieldName: string, fieldValue: number) => void = useCallback(
    (fieldName, fieldValue) => {
      if (fieldValue > 0 && fieldValue <= 100) {
        clearError(fieldName)
      }
    },
    [clearError]
  )

  const validateNotEmpy: (fieldName: string, fieldValue: string) => void = useCallback(
    (fieldName, fieldValue) => {
      if (fieldValue !== "" || fieldValue !== undefined) {
        clearError(fieldName)
      }
    },
    [clearError]
  )

  const validateSumEqual100: (fieldName: string, fieldValue: number, parkingPercentageSum: number) => void = useCallback(
    (fieldName, fieldValue, percentageSum) => {
      if (fieldValue + percentageSum <= 100) {
        clearError(fieldName)
      }
    },
    [clearError]
  )

  const fieldValidators: Partial<
    Record<
      keyof AssignedParking,
      (field: keyof AssignedParking, value: string | number, percentageSum: number | undefined) => void
    >
  > = useMemo(
    () => ({
      parkingId: (field, value) => {
        if (typeof value === "string") {
          validateNotEmpy(field, value)
        }
      },
      percentage: (field, value, parkingPercentageSum) => {
        if (typeof value === "number" && parkingPercentageSum) {
          validate1To100(field, value)
          validateSumEqual100(field, value, parkingPercentageSum)
        }
      },
    }),
    [validateNotEmpy, validate1To100, validateSumEqual100]
  )

  const fieldsToValidate: Array<{
    fieldName: string
    getValue: (affectedParking: AssignedParking) => string | number
    isValueValid: (value: string | number, form: AssignedParking, parkingPercentageSum: number) => boolean
    errorMessage: string
  }> = useMemo(
    () => [
      {
        fieldName: "parkingId",
        getValue: (affectedParking) => affectedParking.parkingId,
        isValueValid: (value, _form) => value !== "" && value !== undefined,
        errorMessage: "Un parking doit être sélectionné",
      },
      {
        fieldName: "percentage",
        getValue: (affectedParking) => affectedParking.percentage,
        isValueValid: (value, _form) => {
          if (typeof value === "number") {
            return value > 0 && value <= 100
          }
          return true
        },
        errorMessage: "Le pourcentage doit être compris entre 1% et 100%",
      },
      {
        fieldName: "percentage",
        getValue: (affectedParking) => affectedParking.percentage,
        isValueValid: (value, _form, parkingPercentageSum) => {
          if (typeof value === "number") {
            return value + parkingPercentageSum <= 100
          } else {
            return false
          }
        },
        errorMessage: "La somme des pourcentages ne peut dépasser 100%.",
      },
    ],
    []
  )

  const validateParkingAssignment: (affectedParking: AssignedParking, parkingPercentageSum: number) => boolean = useCallback(
    (affectedParking, parkingPercentageSum) => {
      let isValid = true

      fieldsToValidate.forEach(({ fieldName, getValue, isValueValid, errorMessage }) => {
        const value = getValue(affectedParking)

        if (!isValueValid(value, affectedParking, parkingPercentageSum)) {
          isValid = false
          setParkingErrors((prev) => ({ ...prev, [fieldName]: errorMessage }))
        }
      })

      return isValid
    },
    [fieldsToValidate]
  )

  const parkingAssignmentStore = useMemo(
    () => ({
      parkingErrors,
      fieldValidators,
      isEditing,
      superstructureOptions,
      infrastructureOptions,
      setParkingErrors,
      setIsEditing,
      validateParkingAssignment,
    }),
    [
      parkingErrors,
      fieldValidators,
      isEditing,
      superstructureOptions,
      infrastructureOptions,
      setParkingErrors,
      setIsEditing,
      validateParkingAssignment,
    ]
  )
  return <ParkingAssignmentContext.Provider value={parkingAssignmentStore}>{children}</ParkingAssignmentContext.Provider>
}

export interface ParkingAssignmentStore {
  parkingErrors: Record<string, string | undefined>
  fieldValidators: Partial<
    Record<
      keyof AssignedParking,
      (field: keyof AssignedParking, value: number | string, parkingPercentageSum: number) => void
    >
  >
  isEditing: boolean
  superstructureOptions: SelectObjOption<BSParking>[]
  infrastructureOptions: SelectObjOption<BSParking>[]
  setParkingErrors: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
  validateParkingAssignment(affectedParking: AssignedParking, parkingPercentageSum: number): boolean
}
